import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datetime";
import moment from "moment";
import {
  MarketingCampaignDoc,
  MarketingCampaignStatus,
  MarketingSequenceDoc,
  MarketingSequenceStatus,
} from "@pollyslack/hub/src/types";
import {
  computeSequenceSendDates,
  sequenceDatesError,
} from "@pollyslack/hub/src/services/dmCampaign/sequenceSchedule";
import * as CampaignContentAPI from "../../services/api/CampaignContent";
import * as SequenceBuilderAPI from "../../services/api/SequenceBuilder";
import { BATCH_SIZE } from "./constants";
import {
  MAX_SEND_DAY,
  MIN_SEND_DAY,
  buildSchedulePreview,
  chunkArray,
  getEligibleSendDates,
  isWeekend,
  parseRecipientsCsv,
} from "./scheduleUtils";
import {
  DEFAULT_SEND_SCHEDULE,
  SendSchedule,
  SendTimeInput,
  describeSendSchedule,
  sendScheduleError,
  sendScheduleParams,
} from "./SendTimeInput";

/**
 * Sequence Builder (PS-5765): build sequences out of Campaign Builder
 * campaigns, then enroll users in them. Sending reuses the individual DM
 * pipeline; see marketingSequenceService.ts.
 */

const STATUS_BADGE_CLASS: Record<MarketingSequenceStatus, string> = {
  [MarketingSequenceStatus.ACTIVE]: "badge-success",
  [MarketingSequenceStatus.PAUSED]: "badge-warning",
  [MarketingSequenceStatus.STOPPED]: "badge-dark",
};

const Spinner: React.FC = () => (
  <span
    className="spinner-border spinner-border-sm mr-2"
    role="status"
    aria-hidden="true"
  />
);

type ViewMode =
  | { kind: "list" }
  | { kind: "form"; sequence?: MarketingSequenceDoc; cloneOf?: boolean }
  | { kind: "enroll"; sequence: MarketingSequenceDoc };

export const SequenceBuilderTab: React.FC = () => {
  const [sequences, setSequences] = useState<MarketingSequenceDoc[]>([]);
  const [campaigns, setCampaigns] = useState<MarketingCampaignDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>({ kind: "list" });
  // Paused sequence whose missed DMs are being moved (resume refuses until then)
  const [rescheduling, setRescheduling] = useState<MarketingSequenceDoc | null>(
    null,
  );

  const campaignsById = useMemo(
    () => new Map(campaigns.map((c) => [c._id, c])),
    [campaigns],
  );

  async function load() {
    setLoading(true);
    try {
      const [sequenceList, campaignList] = await Promise.all([
        SequenceBuilderAPI.listSequences(),
        CampaignContentAPI.listCampaigns(),
      ]);
      setSequences(sequenceList);
      setCampaigns(campaignList);
    } catch (error: any) {
      toast.error(`Failed to load sequences: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function runStatusAction(
    sequence: MarketingSequenceDoc,
    action: "pause" | "resume" | "stop",
  ) {
    const prompts = {
      pause: `Pause "${sequence.title}"? Nothing from it is sent while it's paused, and every DM keeps its date. If a date passes before you resume, you'll be asked to reschedule those DMs first.`,
      resume: `Resume "${sequence.title}"?`,
      stop: `Stop "${sequence.title}"? Every DM it hasn't sent yet is cancelled for everyone, and it can't be restarted (you can clone it).`,
    };
    if (!window.confirm(prompts[action])) {
      return;
    }
    try {
      if (action === "pause") {
        await SequenceBuilderAPI.pauseSequence(sequence._id);
        toast.success(`"${sequence.title}" paused`);
      } else if (action === "resume") {
        await SequenceBuilderAPI.resumeSequence(sequence._id);
        toast.success(`"${sequence.title}" resumed`);
      } else {
        const result = await SequenceBuilderAPI.stopSequence(sequence._id);
        toast.success(
          `"${sequence.title}" stopped; pending DMs cancelled for ${result.usersAffected} user(s)`,
        );
      }
      load();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${action}`);
      // Resume is refused while dates have passed: go straight to fixing them
      if (action === "resume" && /reschedule/i.test(error.message || "")) {
        setRescheduling(sequence);
      }
    }
  }

  if (view.kind === "form") {
    return (
      <SequenceForm
        sequence={view.sequence}
        isClone={view.cloneOf}
        campaigns={campaigns}
        onSaved={() => {
          setView({ kind: "list" });
          load();
        }}
        onCancel={() => setView({ kind: "list" })}
      />
    );
  }

  if (view.kind === "enroll") {
    return (
      <SequenceEnrollPanel
        sequence={view.sequence}
        campaignsById={campaignsById}
        onDone={() => {
          setView({ kind: "list" });
          load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <small className="text-muted">
          Sequences send Campaign Builder campaigns one after another. Weekend
          send dates move to the Monday.
        </small>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setView({ kind: "form" })}
        >
          + Create New Sequence
        </button>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <span className="spinner-border spinner-border-sm" role="status" />
          <span className="ml-2">Loading sequences...</span>
        </div>
      ) : sequences.length === 0 ? (
        <div className="text-center text-muted py-4">
          No sequences yet.{" "}
          <button
            className="btn btn-link p-0"
            onClick={() => setView({ kind: "form" })}
          >
            Create your first sequence
          </button>
        </div>
      ) : (
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th>Title</th>
              <th>DMs</th>
              <th>Cadence</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sequences.map((sequence) => (
              <tr key={sequence._id}>
                <td>
                  <strong>{sequence.title}</strong>
                  <div>
                    <small className="text-muted">
                      Created{" "}
                      {new Date(sequence.createdAt).toLocaleDateString()}
                      {sequence.firstEnrolledAt ? " · has enrollments" : ""}
                    </small>
                  </div>
                </td>
                <td>
                  {sequence.steps.map((step, index) => (
                    <div key={step.campaignId}>
                      <small>
                        DM{index + 1}:{" "}
                        <code>
                          {campaignsById.get(step.campaignId)?.identifier ||
                            "(missing campaign)"}
                        </code>
                      </small>
                    </div>
                  ))}
                </td>
                <td>
                  <small>Every {sequence.cadenceDays} day(s)</small>
                </td>
                <td>
                  <span
                    className={`badge ${
                      STATUS_BADGE_CLASS[sequence.status] || "badge-light"
                    }`}
                  >
                    {sequence.status}
                  </span>
                </td>
                <td style={{ minWidth: 180 }}>
                  <button
                    className="btn btn-outline-primary btn-sm mr-1 mb-1"
                    onClick={() => setView({ kind: "form", sequence })}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm mr-1 mb-1"
                    onClick={() =>
                      setView({ kind: "form", sequence, cloneOf: true })
                    }
                  >
                    Clone
                  </button>
                  {sequence.status !== MarketingSequenceStatus.STOPPED && (
                    <button
                      className="btn btn-outline-success btn-sm mr-1 mb-1"
                      onClick={() => setView({ kind: "enroll", sequence })}
                    >
                      Enroll / Unenroll
                    </button>
                  )}
                  {sequence.status === MarketingSequenceStatus.ACTIVE && (
                    <button
                      className="btn btn-outline-warning btn-sm mr-1 mb-1"
                      onClick={() => runStatusAction(sequence, "pause")}
                    >
                      Pause
                    </button>
                  )}
                  {sequence.status === MarketingSequenceStatus.PAUSED && (
                    <button
                      className="btn btn-outline-success btn-sm mr-1 mb-1"
                      onClick={() => runStatusAction(sequence, "resume")}
                    >
                      Resume
                    </button>
                  )}
                  {sequence.status === MarketingSequenceStatus.PAUSED && (
                    <button
                      className="btn btn-outline-secondary btn-sm mr-1 mb-1"
                      onClick={() => setRescheduling(sequence)}
                    >
                      Reschedule missed DMs
                    </button>
                  )}
                  {sequence.status !== MarketingSequenceStatus.STOPPED && (
                    <button
                      className="btn btn-outline-dark btn-sm mb-1"
                      onClick={() => runStatusAction(sequence, "stop")}
                    >
                      Stop
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rescheduling && (
        <RescheduleModal
          sequence={rescheduling}
          onClose={() => setRescheduling(null)}
        />
      )}
    </div>
  );
};

/**
 * Moves the DMs whose dates passed while a sequence was paused (Ross on
 * PS-5765: keep dates, and ask for new ones when they've passed).
 */
const RescheduleModal: React.FC<{
  sequence: MarketingSequenceDoc;
  onClose: () => void;
}> = ({ sequence, onClose }) => {
  const [nextDate, setNextDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day"),
  );
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      const result = await SequenceBuilderAPI.rescheduleSequence(
        sequence._id,
        nextDate.format("YYYY-MM-DD"),
      );
      if (result.usersBusy > 0) {
        // Still overdue, so resume would refuse; another run picks them up
        toast.warn(
          `Moved missed DMs for ${result.usersRescheduled} people, but ${result.usersBusy} were being sent other DMs. Reschedule again in a minute, then resume.`,
        );
      } else {
        toast.success(
          result.usersRescheduled === 0
            ? "Nobody had a missed DM. You can resume now."
            : `Moved missed DMs for ${result.usersRescheduled} ${
                result.usersRescheduled === 1 ? "person" : "people"
              } (${result.dmsMoved} DMs). You can resume now.`,
        );
      }
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Failed to reschedule");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="modal"
      tabIndex={-1}
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Reschedule missed DMs</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <p className="mb-3">
              Everyone in <strong>{sequence.title}</strong> whose next DM date
              has passed gets that DM on the date below instead, at the same
              time of day. Their later DMs move by the same number of days, so
              the gaps stay the same. People whose DMs are all still ahead
              aren&apos;t changed.
            </p>
            <label htmlFor="rescheduleDate" className="font-weight-bold">
              New date for each person&apos;s next DM
            </label>
            <DatePicker
              value={nextDate}
              onChange={(date) =>
                moment.isMoment(date) && setNextDate(date.startOf("day"))
              }
              timeFormat={false}
              isValidDate={(current: moment.Moment) =>
                current.isSameOrAfter(moment().startOf("day"))
              }
              inputProps={{ id: "rescheduleDate", className: "form-control" }}
            />
            <small className="form-text text-muted">
              A weekend date moves to the Monday. If someone&apos;s send time on
              that date has already passed, theirs goes the next day.
            </small>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={saving}
            >
              {saving && <Spinner />}
              Reschedule
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SequenceFormProps {
  /** Sequence being edited, or the source of a clone */
  sequence?: MarketingSequenceDoc;
  isClone?: boolean;
  campaigns: MarketingCampaignDoc[];
  onSaved: () => void;
  onCancel: () => void;
}

const SequenceForm: React.FC<SequenceFormProps> = ({
  sequence,
  isClone,
  campaigns,
  onSaved,
  onCancel,
}) => {
  const isEdit = !!sequence && !isClone;
  // A stopped sequence only takes a new title; clone it to change more
  const structureLocked =
    isEdit && sequence.status === MarketingSequenceStatus.STOPPED;
  // Edits to DMs or cadence get applied to the people already enrolled
  const hasEnrollments = isEdit && !!sequence.firstEnrolledAt;

  const [title, setTitle] = useState(
    isClone ? `${sequence.title} (copy)` : sequence?.title || "",
  );
  // Text, not a dropdown: any whole number of days (PS-5765)
  const [cadenceText, setCadenceText] = useState(
    String(sequence?.cadenceDays ?? 7),
  );
  const [stepIds, setStepIds] = useState<string[]>(
    sequence?.steps.map((step) => step.campaignId) || [""],
  );
  const [saving, setSaving] = useState(false);

  const selectableCampaigns = campaigns.filter(
    (c) => c.status !== MarketingCampaignStatus.ARCHIVED,
  );
  const cadenceDays = /^\d+$/.test(cadenceText.trim())
    ? parseInt(cadenceText.trim(), 10)
    : NaN;

  function moveStep(index: number, delta: -1 | 1) {
    setStepIds((ids) => {
      const next = [...ids];
      const target = index + delta;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSave() {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!(cadenceDays >= 1 && cadenceDays <= 365)) {
      toast.error("Cadence must be a whole number of days from 1 to 365");
      return;
    }
    if (stepIds.length === 0 || stepIds.some((id) => !id)) {
      toast.error("Pick a campaign for every DM");
      return;
    }
    if (new Set(stepIds).size !== stepIds.length) {
      toast.error("A campaign can only appear once in a sequence");
      return;
    }

    const steps = stepIds.map((campaignId) => ({ campaignId }));
    const structureChanged =
      isEdit &&
      (cadenceDays !== sequence.cadenceDays ||
        JSON.stringify(stepIds) !==
          JSON.stringify(sequence.steps.map((step) => step.campaignId)));
    if (
      hasEnrollments &&
      structureChanged &&
      !window.confirm(
        "People are enrolled in this sequence. Their unsent DMs will be updated: each person's next DM keeps its date, and the rest follow the new DMs and cadence. Continue?",
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const updated = await SequenceBuilderAPI.updateSequence(
          sequence._id,
          structureLocked
            ? { title: title.trim() }
            : { title: title.trim(), steps, cadenceDays },
        );
        toast.success(
          updated.updatingEnrolled
            ? "Sequence updated. Enrolled people's unsent DMs are being updated; a summary posts to #project-dm-campaign."
            : "Sequence updated",
        );
      } else {
        await SequenceBuilderAPI.createSequence({
          title: title.trim(),
          steps,
          cadenceDays,
        });
        toast.success(isClone ? "Sequence cloned" : "Sequence created");
      }
      onSaved();
    } catch (error: any) {
      toast.error(error.message || "Failed to save sequence");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h5 className="mb-3">
        {isEdit ? "Edit Sequence" : isClone ? "Clone Sequence" : "New Sequence"}
      </h5>

      {structureLocked && (
        <div className="alert alert-info py-2">
          <small>
            This sequence is stopped, so only the title can change. Clone it to
            make a changed version.
          </small>
        </div>
      )}
      {hasEnrollments && !structureLocked && (
        <div className="alert alert-warning py-2">
          <small>
            People are enrolled. Changing the DMs or cadence updates their
            unsent DMs: each person&apos;s next DM keeps its date and the rest
            follow the new list and cadence. DMs already sent aren&apos;t
            touched, and every DM must be an active campaign.
          </small>
        </div>
      )}

      <div className="form-group">
        <label className="font-weight-bold" htmlFor="sequenceTitle">
          Sequence Title
        </label>
        <input
          id="sequenceTitle"
          className="form-control"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Q4 Presentations nurture"
        />
      </div>

      <div className="form-group">
        <label className="font-weight-bold" htmlFor="sequenceCadence">
          Cadence (days between DMs)
        </label>
        <input
          id="sequenceCadence"
          className="form-control"
          style={{ maxWidth: 160 }}
          type="text"
          inputMode="numeric"
          value={cadenceText}
          disabled={structureLocked}
          onChange={(e) => setCadenceText(e.target.value)}
        />
        <small className="form-text text-muted">
          Any whole number of days. A DM that lands on a Saturday or Sunday goes
          out on the Monday, and the next DM counts from there.
        </small>
      </div>

      <div className="form-group">
        <label className="font-weight-bold">DMs (in send order)</label>
        {selectableCampaigns.length === 0 && (
          <div className="alert alert-warning py-2">
            <small>
              No campaigns yet. Create them in the Campaign Builder tab first.
            </small>
          </div>
        )}
        {stepIds.map((campaignId, index) => {
          const campaign = campaigns.find((c) => c._id === campaignId);
          return (
            <div className="d-flex align-items-center mb-2" key={index}>
              <span className="mr-2 font-weight-bold" style={{ width: 48 }}>
                DM{index + 1}
              </span>
              <select
                className="form-control mr-2"
                value={campaignId}
                disabled={structureLocked}
                onChange={(e) =>
                  setStepIds((ids) =>
                    ids.map((id, i) => (i === index ? e.target.value : id)),
                  )
                }
              >
                <option value="">Select a campaign…</option>
                {selectableCampaigns.map((c) => (
                  <option
                    key={c._id}
                    value={c._id}
                    disabled={c._id !== campaignId && stepIds.includes(c._id)}
                  >
                    {c.identifier} — {c.headerText}
                    {c.status === MarketingCampaignStatus.DRAFT
                      ? " (draft)"
                      : ""}
                  </option>
                ))}
                {campaign?.status === MarketingCampaignStatus.ARCHIVED && (
                  <option value={campaign._id}>
                    {campaign.identifier} (archived)
                  </option>
                )}
              </select>
              <button
                className="btn btn-outline-secondary btn-sm mr-1"
                title="Move up"
                disabled={structureLocked || index === 0}
                onClick={() => moveStep(index, -1)}
              >
                ↑
              </button>
              <button
                className="btn btn-outline-secondary btn-sm mr-1"
                title="Move down"
                disabled={structureLocked || index === stepIds.length - 1}
                onClick={() => moveStep(index, 1)}
              >
                ↓
              </button>
              <button
                className="btn btn-outline-danger btn-sm"
                title="Remove"
                disabled={structureLocked || stepIds.length === 1}
                onClick={() =>
                  setStepIds((ids) => ids.filter((_, i) => i !== index))
                }
              >
                ✕
              </button>
            </div>
          );
        })}
        {!structureLocked && (
          <button
            className="btn btn-link btn-sm p-0"
            onClick={() => setStepIds((ids) => [...ids, ""])}
          >
            + Add DM
          </button>
        )}
        <small className="form-text text-muted">
          Draft campaigns can be added now but must be activated before anyone
          is enrolled.
        </small>
      </div>

      <div className="mb-4">
        <button
          className="btn btn-primary mr-2"
          onClick={handleSave}
          disabled={saving}
        >
          {saving && <Spinner />}
          {isEdit ? "Update" : isClone ? "Create Clone" : "Create Sequence"}
        </button>
        <button className="btn btn-link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

interface SequenceEnrollPanelProps {
  sequence: MarketingSequenceDoc;
  campaignsById: Map<string, MarketingCampaignDoc>;
  onDone: () => void;
}

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * People who start the sequence together, with every DM's date: the whole
 * list in start date mode, or one CSV Send Date day in range mode.
 */
interface EnrollGroup {
  /** CSV Send Date day; unset in start date mode */
  day?: number;
  userIds: string[];
  dates: moment.Moment[];
}

const SequenceEnrollPanel: React.FC<SequenceEnrollPanelProps> = ({
  sequence,
  campaignsById,
  onDone,
}) => {
  const [action, setAction] = useState<"enroll" | "unenroll">("enroll");
  const [scheduleMode, setScheduleMode] = useState<"single" | "range">(
    "single",
  );
  const [startDate, setStartDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day"),
  );
  const [dmDates, setDmDates] = useState<moment.Moment[]>([]);
  const [rangeStartDate, setRangeStartDate] = useState<moment.Moment>(
    moment().add(1, "day").startOf("day"),
  );
  const [rangeEndDate, setRangeEndDate] = useState<moment.Moment>(
    moment().add(14, "days").startOf("day"),
  );
  const [csvSchedule, setCsvSchedule] = useState<{
    fileName: string;
    dayByUserId: Record<string, number>;
    invalidDayUserIds: string[];
  } | null>(null);
  const [groups, setGroups] = useState<EnrollGroup[] | null>(null);
  const [defaultedUserCount, setDefaultedUserCount] = useState(0);
  const [sendSchedule, setSendSchedule] = useState<SendSchedule>(
    DEFAULT_SEND_SCHEDULE,
  );
  const [bypassPaidPlanCheck, setBypassPaidPlanCheck] = useState(false);
  const [userIdsText, setUserIdsText] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const userIds = userIdsText.split(/[\s,]+/).filter(Boolean);
  const steps = sequence.steps.map((step) =>
    campaignsById.get(step.campaignId),
  );
  const inactiveStep = steps.findIndex(
    (c) => !c || c.status !== MarketingCampaignStatus.ACTIVE,
  );

  const cadenceDates = (start: moment.Moment) =>
    computeSequenceSendDates(
      start.format(DATE_FORMAT),
      sequence.steps.length,
      sequence.cadenceDays,
    ).map((date) => moment(date, DATE_FORMAT));

  // Every DM's date goes back to the cadence when the start date or the
  // sequence changes; after that each one can be moved (e.g. off a holiday)
  const startKey = startDate.format(DATE_FORMAT);
  useEffect(() => {
    setDmDates(
      computeSequenceSendDates(
        startKey,
        sequence.steps.length,
        sequence.cadenceDays,
      ).map((date) => moment(date, DATE_FORMAT)),
    );
  }, [startKey, sequence.steps.length, sequence.cadenceDays]);

  const isValidDate = (current: moment.Moment) =>
    current.isSameOrAfter(moment().startOf("day"));
  // DMs never go out on a weekend
  const isValidSendDay = (current: moment.Moment) =>
    isValidDate(current) && !isWeekend(current);

  const datesError = (dates: moment.Moment[]) =>
    sequenceDatesError(
      dates.map((date) => date.format(DATE_FORMAT)),
      sequence.steps.length,
    );

  /** Sends the start date alone unless a DM's date was changed by hand */
  const datesPayload = (dates: moment.Moment[]) => {
    const formatted = dates.map((date) => date.format(DATE_FORMAT));
    const byCadence = computeSequenceSendDates(
      formatted[0],
      sequence.steps.length,
      sequence.cadenceDays,
    );
    return byCadence.join() === formatted.join()
      ? { startDate: formatted[0] }
      : { dmDates: formatted };
  };

  function updateDmDate(index: number, value: moment.Moment | string) {
    // react-datetime emits a string while the typed value is unparseable
    if (!moment.isMoment(value) || !isValidSendDay(value)) return;
    const date = value.clone().startOf("day");
    setDmDates((dates) => dates.map((d, i) => (i === index ? date : d)));
  }

  function updateGroupDate(
    groupIndex: number,
    dmIndex: number,
    value: moment.Moment | string,
  ) {
    if (!moment.isMoment(value) || !isValidSendDay(value)) return;
    const date = value.clone().startOf("day");
    setGroups((current) =>
      current
        ? current.map((group, g) =>
            g === groupIndex
              ? {
                  ...group,
                  dates: group.dates.map((d, i) => (i === dmIndex ? date : d)),
                }
              : group,
          )
        : current,
    );
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseRecipientsCsv(e.target?.result as string);
      if (!parsed || parsed.userIds.length === 0) {
        toast.error("CSV must have a header row and at least one user ID.");
        return;
      }
      setUserIdsText(parsed.userIds.join("\n"));
      setCsvSchedule(
        parsed.hasSendDateColumn
          ? {
              fileName: file.name,
              dayByUserId: parsed.dayByUserId,
              invalidDayUserIds: parsed.invalidDayUserIds,
            }
          : null,
      );
      toast.info(`Loaded ${parsed.userIds.length} user IDs from ${file.name}`);
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  /** One group per CSV Send Date day, each starting DM1 on its day */
  function buildRangeGroups(): EnrollGroup[] | null {
    if (!csvSchedule) {
      toast.error(
        "Date range scheduling needs a CSV upload with a Send Date column.",
      );
      return null;
    }
    if (rangeEndDate.isBefore(rangeStartDate, "day")) {
      toast.error("End date cannot be before the start date.");
      return null;
    }
    const { preview, error } = buildSchedulePreview(
      userIds,
      csvSchedule.dayByUserId,
      getEligibleSendDates(rangeStartDate, rangeEndDate),
    );
    if (error || !preview) {
      toast.error(error || "Failed to build the schedule.");
      return null;
    }
    const userIdsByDay = new Map<number, string[]>();
    userIds.forEach((userId) => {
      const day = csvSchedule.dayByUserId[userId] || MIN_SEND_DAY;
      userIdsByDay.set(day, [...(userIdsByDay.get(day) || []), userId]);
    });
    setDefaultedUserCount(preview.defaultedUserCount);
    return preview.rows.map((row) => ({
      day: row.day,
      userIds: userIdsByDay.get(row.day) || [],
      dates: cadenceDates(row.date),
    }));
  }

  function requestConfirmation() {
    if (userIds.length === 0) {
      toast.error("Please enter at least one user ID.");
      return;
    }
    if (action === "enroll") {
      if (sendScheduleError(sendSchedule)) {
        toast.error(sendScheduleError(sendSchedule));
        return;
      }
      if (inactiveStep !== -1) {
        toast.error(
          `DM${
            inactiveStep + 1
          }'s campaign isn't active. Activate it in the Campaign Builder first.`,
        );
        return;
      }
      if (scheduleMode === "single") {
        const error = datesError(dmDates);
        if (error) {
          toast.error(error);
          return;
        }
        setGroups([{ userIds, dates: dmDates }]);
      } else {
        const rangeGroups = buildRangeGroups();
        if (!rangeGroups) return;
        setGroups(rangeGroups);
      }
    }
    setConfirming(true);
  }

  function submit() {
    const enrollGroups = action === "enroll" ? groups || [] : [];
    for (const group of enrollGroups) {
      const error = datesError(group.dates);
      if (error) {
        toast.error(group.day ? `Day ${group.day}: ${error}` : error);
        return;
      }
    }
    setConfirming(false);
    setSubmitting(true);
    setSuccessMessage(null);
    const requests: { batch: string[]; send: () => Promise<unknown> }[] =
      action === "enroll"
        ? enrollGroups.flatMap((group) =>
            chunkArray(group.userIds, BATCH_SIZE).map((batch) => ({
              batch,
              send: () =>
                SequenceBuilderAPI.enrollInSequence(sequence._id, {
                  userIds: batch,
                  ...datesPayload(group.dates),
                  ...sendScheduleParams(sendSchedule),
                  bypassPaidPlanCheck,
                }),
            })),
          )
        : chunkArray(userIds, BATCH_SIZE).map((batch) => ({
            batch,
            send: () =>
              SequenceBuilderAPI.unenrollFromSequence(sequence._id, batch),
          }));
    // Fire batches in parallel without waiting, like the other tabs; the
    // per-batch summary is posted to #project-dm-campaign
    requests.forEach(({ batch, send }) =>
      send().catch((error: any) =>
        toast.error(
          `${action === "enroll" ? "Enroll" : "Unenroll"} batch of ${
            batch.length
          } users failed: ${error.message}`,
        ),
      ),
    );
    setSuccessMessage(
      `${action === "enroll" ? "Enrollment" : "Unenrollment"} submitted for ${
        userIds.length
      } users (${requests.length} batch${
        requests.length === 1 ? "" : "es"
      }). Results will be posted in the #project-dm-campaign channel.`,
    );
    setSubmitting(false);
  }

  const rangeWeekdayCount = getEligibleSendDates(
    rangeStartDate,
    rangeEndDate,
  ).length;
  const confirmingRange =
    action === "enroll" && scheduleMode === "range" && !!groups;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{sequence.title}</h5>
        <button className="btn btn-link btn-sm" onClick={onDone}>
          ← Back to sequences
        </button>
      </div>

      {sequence.status === MarketingSequenceStatus.PAUSED && (
        <div className="alert alert-warning py-2">
          <small>
            This sequence is paused. New enrollments are scheduled but nothing
            sends until it&apos;s resumed.
          </small>
        </div>
      )}

      <div className="d-flex mb-4" role="group">
        <button
          type="button"
          className={`btn w-100 mr-2 ${
            action === "enroll" ? "btn-success" : "btn-outline-success"
          }`}
          onClick={() => setAction("enroll")}
        >
          Enroll Users
        </button>
        <button
          type="button"
          className={`btn w-100 ${
            action === "unenroll"
              ? "btn-warning text-white"
              : "btn-outline-warning text-dark"
          }`}
          onClick={() => setAction("unenroll")}
        >
          Unenroll Users
        </button>
      </div>

      {action === "enroll" ? (
        <>
          <div
            className="btn-group btn-group-sm mb-3"
            role="group"
            aria-label="Schedule mode"
          >
            <button
              type="button"
              id="sequenceScheduleSingle"
              className={`btn ${
                scheduleMode === "single"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setScheduleMode("single")}
            >
              Single start date
            </button>
            <button
              type="button"
              id="sequenceScheduleRange"
              className={`btn ${
                scheduleMode === "range" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setScheduleMode("range")}
            >
              Date range (CSV)
            </button>
          </div>

          <div className="form-row mb-3 align-items-start">
            {scheduleMode === "single" ? (
              <div className="col-md-4">
                <label htmlFor="sequenceStartDate" className="mb-2">
                  Start Date (DM1)
                </label>
                <DatePicker
                  value={startDate}
                  onChange={(date) =>
                    moment.isMoment(date) && setStartDate(date.startOf("day"))
                  }
                  timeFormat={false}
                  isValidDate={isValidDate}
                  inputProps={{
                    id: "sequenceStartDate",
                    className: "form-control",
                  }}
                />
              </div>
            ) : (
              <>
                <div className="col-md-4">
                  <label htmlFor="sequenceRangeStart" className="mb-2">
                    Start Date
                  </label>
                  <DatePicker
                    value={rangeStartDate}
                    onChange={(date) =>
                      moment.isMoment(date) &&
                      setRangeStartDate(date.startOf("day"))
                    }
                    timeFormat={false}
                    isValidDate={isValidDate}
                    inputProps={{
                      id: "sequenceRangeStart",
                      className: "form-control",
                    }}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="sequenceRangeEnd" className="mb-2">
                    End Date
                  </label>
                  <DatePicker
                    value={rangeEndDate}
                    onChange={(date) =>
                      moment.isMoment(date) &&
                      setRangeEndDate(date.startOf("day"))
                    }
                    timeFormat={false}
                    isValidDate={(current: moment.Moment) =>
                      isValidDate(current) &&
                      current.isSameOrAfter(rangeStartDate, "day")
                    }
                    inputProps={{
                      id: "sequenceRangeEnd",
                      className: "form-control",
                    }}
                  />
                </div>
              </>
            )}
            <div
              className={scheduleMode === "single" ? "col-md-8" : "col-12 mt-3"}
            >
              <SendTimeInput
                id="sequenceSendTime"
                value={sendSchedule}
                onChange={setSendSchedule}
              />
              <small className="form-text text-muted">
                In each user&apos;s own timezone, within 30 minutes. With a
                window, each person keeps their slot for every DM.
              </small>
            </div>
          </div>

          {scheduleMode === "single" ? (
            <>
              <label className="font-weight-bold mb-2">Schedule</label>
              <table className="table table-sm table-bordered mb-1">
                <tbody>
                  {steps.map((campaign, index) => (
                    <tr key={sequence.steps[index].campaignId}>
                      <td className="align-middle" style={{ width: 60 }}>
                        DM{index + 1}
                      </td>
                      <td className="align-middle">
                        <code>
                          {campaign?.identifier || "(missing campaign)"}
                        </code>
                        {campaign &&
                          campaign.status !==
                            MarketingCampaignStatus.ACTIVE && (
                            <span className="badge badge-danger ml-2">
                              {campaign.status}
                            </span>
                          )}
                      </td>
                      <td style={{ width: 220 }}>
                        {index === 0 || !dmDates[index] ? (
                          <strong>
                            {dmDates[index]?.format("ddd MMM D, YYYY")}
                          </strong>
                        ) : (
                          <DatePicker
                            value={dmDates[index]}
                            onChange={(date) => updateDmDate(index, date)}
                            timeFormat={false}
                            dateFormat="ddd MMM D, YYYY"
                            isValidDate={isValidSendDay}
                            inputProps={{
                              id: `sequenceDmDate-${index}`,
                              className: "form-control form-control-sm",
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <small className="form-text text-muted mb-3">
                Change a DM&apos;s date to move it off a holiday. Changing the
                start date puts them back on the {sequence.cadenceDays}-day
                cadence.
              </small>
            </>
          ) : (
            <>
              <small className="form-text text-muted">
                Day N in the CSV Send Date column maps to the Nth weekday in
                this range ({rangeWeekdayCount} weekday
                {rangeWeekdayCount === 1 ? "" : "s"}, supports days 1 to{" "}
                {Math.min(rangeWeekdayCount, MAX_SEND_DAY)}). Each day&apos;s
                users get DM1 on that day and the rest every{" "}
                {sequence.cadenceDays} day(s); every date can be changed when
                you confirm.
              </small>
              {csvSchedule ? (
                <div className="alert alert-success py-2 px-3 mt-2 mb-3">
                  <small>
                    Send Date column loaded from{" "}
                    <strong>{csvSchedule.fileName}</strong>:{" "}
                    {Object.keys(csvSchedule.dayByUserId).length} users with day
                    values
                    {csvSchedule.invalidDayUserIds.length > 0
                      ? `, ${csvSchedule.invalidDayUserIds.length} missing or invalid (default to Day 1)`
                      : ""}
                    .
                  </small>
                </div>
              ) : (
                <div className="alert alert-warning py-2 px-3 mt-2 mb-3">
                  <small>
                    Upload a CSV with a Send Date column (values 1 to{" "}
                    {MAX_SEND_DAY}) to use this mode.
                  </small>
                </div>
              )}
            </>
          )}

          <div className="custom-control custom-checkbox mb-3">
            <input
              type="checkbox"
              className="custom-control-input"
              id="sequenceBypassPaidPlan"
              checked={bypassPaidPlanCheck}
              onChange={(e) => setBypassPaidPlanCheck(e.target.checked)}
            />
            <label
              className="custom-control-label"
              htmlFor="sequenceBypassPaidPlan"
            >
              Keep sending to users on a paid plan
            </label>
            <small className="form-text text-muted">
              By default a user who is on a paid plan when a DM comes due is
              unenrolled from the rest of the sequence, like the existing
              sequences.
            </small>
          </div>
        </>
      ) : (
        <div className="alert alert-warning">
          <small>
            Removes these users&apos; unsent DMs from this sequence. DMs that
            already went out stay in their history.
          </small>
        </div>
      )}

      <div className="form-group">
        <label className="font-weight-bold mb-2" htmlFor="sequenceUserIds">
          User IDs
        </label>
        <div className="mb-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV
          </button>
          <small className="text-muted ml-2">
            User mongo IDs in the first column, with a header row. An optional
            Send Date column (1 to {MAX_SEND_DAY}) is used in date range mode.
          </small>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            style={{ display: "none" }}
          />
        </div>
        <textarea
          id="sequenceUserIds"
          className="form-control"
          rows={5}
          value={userIdsText}
          onChange={(e) => setUserIdsText(e.target.value)}
          placeholder="Enter user mongo IDs separated by commas, spaces, or new lines, or upload a CSV."
        />
      </div>

      <div className="d-flex justify-content-end">
        <button
          className={`btn ${
            action === "enroll" ? "btn-success" : "btn-warning text-white"
          }`}
          onClick={requestConfirmation}
          disabled={submitting}
        >
          {submitting && <Spinner />}
          {action === "enroll" ? "Enroll Users" : "Unenroll Users"}
        </button>
      </div>

      {successMessage && (
        <div className="alert alert-success mt-4" role="alert">
          {successMessage}
        </div>
      )}

      {confirming && (
        <div
          className="modal"
          tabIndex={-1}
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className={`modal-dialog modal-dialog-centered ${
              confirmingRange ? "modal-xl" : ""
            }`}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {action === "enroll"
                    ? "Confirm Enrollment"
                    : "Confirm Unenrollment"}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setConfirming(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <ul className="list-group">
                  <li className="list-group-item d-flex justify-content-between">
                    <span className="mr-3">Sequence</span>
                    <strong className="text-right">{sequence.title}</strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Users</span>
                    <strong>{userIds.length}</strong>
                  </li>
                  {action === "enroll" && (
                    <>
                      <li className="list-group-item d-flex justify-content-between">
                        <span>Send Time</span>
                        <strong>
                          {describeSendSchedule(sendSchedule)} in each
                          user&apos;s timezone
                        </strong>
                      </li>
                      {!confirmingRange &&
                        dmDates.map((date, index) => (
                          <li
                            key={index}
                            className="list-group-item d-flex justify-content-between"
                          >
                            <span>DM{index + 1}</span>
                            <strong>{date.format("ddd MMM D, YYYY")}</strong>
                          </li>
                        ))}
                      <li className="list-group-item d-flex justify-content-between">
                        <span className="mr-3">Paid plan users</span>
                        {bypassPaidPlanCheck ? (
                          <span className="badge badge-warning text-dark">
                            Keep sending
                          </span>
                        ) : (
                          <span className="text-right">
                            Dropped when their next DM is due
                          </span>
                        )}
                      </li>
                    </>
                  )}
                </ul>
                {confirmingRange && groups && (
                  <div className="mt-3">
                    {defaultedUserCount > 0 && (
                      <div className="alert alert-warning py-2">
                        {defaultedUserCount} user(s) have a missing or invalid
                        Send Date value and will default to Day 1.
                      </div>
                    )}
                    <label className="font-weight-bold mb-2">
                      Schedule (dates are editable)
                    </label>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered mb-2">
                        <thead>
                          <tr>
                            <th>Day</th>
                            <th>Users</th>
                            {sequence.steps.map((_, index) => (
                              <th key={index} style={{ minWidth: 150 }}>
                                DM{index + 1}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((group, groupIndex) => (
                            <tr key={group.day}>
                              <td className="align-middle">Day {group.day}</td>
                              <td className="align-middle">
                                <span className="badge badge-primary badge-pill">
                                  {group.userIds.length}
                                </span>
                              </td>
                              {group.dates.map((date, dmIndex) => (
                                <td key={dmIndex}>
                                  <DatePicker
                                    value={date}
                                    onChange={(value) =>
                                      updateGroupDate(
                                        groupIndex,
                                        dmIndex,
                                        value,
                                      )
                                    }
                                    timeFormat={false}
                                    isValidDate={isValidSendDay}
                                    inputProps={{
                                      id: `sequenceGroupDate-${group.day}-${dmIndex}`,
                                      className: "form-control form-control-sm",
                                    }}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <small className="form-text text-muted">
                      Change any date before confirming, e.g. to move a DM off a
                      holiday. Each DM has to come after the one before it.
                    </small>
                  </div>
                )}
                {action === "enroll" && (
                  <small className="form-text text-muted">
                    Users who already received (or are scheduled for) one of
                    these campaigns skip that DM.
                  </small>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  className={`btn ${
                    action === "enroll"
                      ? "btn-success"
                      : "btn-warning text-white"
                  }`}
                  onClick={submit}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
